import React from 'react';

import { cn } from '@/lib/utils';

import ImageSB, { ImageSBProps } from '@/components/atoms/image/ImageSB';
import LinkButtonSB, {
  LinkButtonSBProps,
} from '@/components/atoms/linkButton/LinkSB';
import RichTextFieldSB, {
  RichTextFieldSBProps,
} from '@/components/atoms/richTextField/RichTextFieldSB';
import { BlockComponentModel } from '@/components/cms/types/blok-component';
import { Table, TableProps } from '@/components/organisms/table';

import {
  BlockHeader,
  BlockHeaderContent,
} from '@/features/utils/BlockHeaderContent';
import { BlockWrapper } from '@/features/utils/BlockWrapper';

interface TableStrapiProps {
  header?: BlockHeader;
  variant?: TableProps['variant'];
  rows: {
    columns: {
      icon?: ImageSBProps;
      text?: RichTextFieldSBProps;
      button?: LinkButtonSBProps;
      align?: 'left' | 'center';
    }[];
  }[];
}

function Column({
  icon,
  text,
  button,
  align,
  classNames,
}: TableStrapiProps['rows'][0]['columns'][0] & {
  classNames?: {
    root?: string;
  };
}) {
  return (
    <div
      className={cn(
        'flex w-full flex-col gap-3 self-start',
        align === 'center' ? 'justify-center' : 'justify-start',
        classNames?.root
      )}
    >
      {(icon || text) && (
        <div
          className={cn(
            'flex w-full flex-row gap-3 self-start ',
            align === 'center' ? 'justify-center' : 'justify-start'
          )}
        >
          {icon && (
            <ImageSB
              block={{ ...icon }}
              inner={{ classNames: { image: 'w-6' } }}
            />
          )}
          {text && <RichTextFieldSB block={text} />}
        </div>
      )}
      {button && <LinkButtonSB block={button} inner={{ fullwidth: true }} />}
    </div>
  );
}

export function TableStrapi({
  block,
  ...rest
}: BlockComponentModel<TableStrapiProps>) {
  const { rows, header, variant = 'default' } = block;

  const head = rows?.[0].columns.map((column, index) => ({
    key: `row-0-col-${index}`,
    value: <Column {...column} />,
    hasHoverEffect: false,
  })) satisfies TableProps['head'];

  let body = rows
    .map((row, rowIndex) =>
      row.columns.map((column, columnIndex) => ({
        key: `row-${rowIndex}-col-${columnIndex}`,
        value: <Column {...column} />,
        hasHoverEffect: true,
      }))
    )
    .slice(1) as TableProps['body'];
  if (variant === 'extra-left-column') {
    body = body.slice(0, -1);
  }

  const footer =
    variant === 'extra-left-column'
      ? (rows?.[rows.length - 1].columns.map((column, index) => ({
          key: `row-${rows.length - 1}-col-${index}`,
          value: <Column {...column} />,
          hasHoverEffect: false,
        })) satisfies TableProps['footer'])
      : undefined;

  return (
    <BlockWrapper {...rest} theme={header?.theme?.themeKey}>
      <div data-tracker-section='table'>
        <BlockHeaderContent
          {...header}
          classNames={{
            children: 'overflow-auto',
          }}
        >
          <Table head={head} body={body} footer={footer} variant={variant} />
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
}
